import pako from 'pako';

/**
 * URPFileHandler - Handles reading and decompressing .urp files
 * 
 * Responsible for:
 * - Reading .urp files (gzipped XML)
 * - Decompressing gzipped content
 * - Providing raw XML access
 */
export class URPFileHandler {
  constructor() {
    this.xmlDoc = null;
    this.rawXML = '';
    this.fileName = '';
  }

  /**
   * Parse a .urp file (which is a gzipped XML file)
   * @param {File} file - The .urp file from user input
   * @returns {Promise<{xmlDoc: Document, rawXML: string, fileName: string}>} Parsed XML document and raw XML
   */
  async parseFile(file) {
    this.fileName = file.name;
    
    try {
      const arrayBuffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      return this.parseData(uint8Array, file.name);
    } catch (error) {
      console.error('Error parsing URP file:', error);
      throw new Error(`Failed to parse .urp file: ${error.message}`);
    }
  }

  /**
   * Parse URP data from Uint8Array (for robot downloads or other binary data)
   * @param {Uint8Array} uint8Array - The compressed or uncompressed URP data
   * @param {string} fileName - Optional filename
   * @returns {{xmlDoc: Document, rawXML: string, fileName: string}} Parsed XML document
   */
  parseData(uint8Array, fileName = 'unknown.urp') {
    this.fileName = fileName;
    
    try {
      let decompressed;
      
      // Try to decompress - if it fails, assume it's already uncompressed XML
      try {
        decompressed = pako.inflate(uint8Array, { to: 'string' });
      } catch (decompressError) {
        // If decompression fails, try treating it as plain text XML
        console.log('File not gzipped, treating as plain XML');
        const decoder = new TextDecoder('utf-8');
        decompressed = decoder.decode(uint8Array);
      }
      
      this.rawXML = decompressed;
      
      // Parse XML
      const parser = new DOMParser();
      this.xmlDoc = parser.parseFromString(decompressed, 'text/xml');
      
      // Check for parsing errors
      const parserError = this.xmlDoc.querySelector('parsererror');
      if (parserError) {
        throw new Error('XML parsing error: ' + parserError.textContent);
      }
      
      return {
        xmlDoc: this.xmlDoc,
        rawXML: this.rawXML,
        fileName: this.fileName
      };
      
    } catch (error) {
      console.error('Error parsing URP data:', error);
      throw new Error(`Failed to parse .urp data: ${error.message}`);
    }
  }

  /**
   * Get the current XML as a string
   * @returns {string} XML content
   */
  getXML() {
    return this.rawXML;
  }

  /**
   * Update the raw XML string from the current DOM
   */
  updateRawXML() {
    if (this.xmlDoc) {
      this.rawXML = new XMLSerializer().serializeToString(this.xmlDoc);
    }
  }

  /**
   * Get program name from XML
   * @returns {string} Program name
   */
  getProgramName() {
    if (!this.xmlDoc) return 'Unknown';
    const root = this.xmlDoc.documentElement;
    return root.getAttribute('name') || 'Unknown';
  }

  /**
   * Get robot serial number
   * @returns {string} Robot serial number
   */
  getRobotSerial() {
    if (!this.xmlDoc) return 'Unknown';
    const root = this.xmlDoc.documentElement;
    return root.getAttribute('robotSerialNumber') || 'Unknown';
  }
}
